import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { GzTrialHomePageWebComponent } from '../../gzTrialHomePage';
import { AvailableInProductTrialsDetails, InProductTrialsAppViewData } from '../types/AvailableInProductTrialsDetails';
import {
  getHomePageMainProductItemsConfig,
  getHomePageSecondaryCardItemsConfig,
} from './gzPageConfigs/gzHomePageConfig/gz-home-page-main-product-items-config';
import {
  getLandingPageConfigForTrialType,
  getTranslationsForCountdownByReason,
} from '../../../common/gzLandingPage/view/gzLandingPageConfig/gz-landing-page-config';
import { AvailableProductTrialType } from '../../types/AvailableProductTrialType';
import { GzLandingPageConfig } from '../../../common/gzLandingPage/types';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { TemplateState } from '../../../../types/common/types';
import { GzImageRepoWebComponent } from '../../../../ui/gzImageRepo';
import { ImageState } from '../../../../ui/gzImageRepo/types';
import { GzInProductTrialsHeaderWebComponent } from '../../inProductTrialsHeader';
import { GzTextWebComponent } from '../../../../ui/gzText';
import { GzLinkWebComponent } from '../../../../ui/gzLinkComponent';
import { htmlEncode } from '../../../../utils/escape.utils';
import { CountdownTranslations } from '../../../../ui/gzCountDown/types';
import { getTranslatedInProductTrialType } from '../../utils/InProductTrialType';
import { B2bLandingPageWebComponent } from '../../b2bLandingPage';
import { ProductLandingPageBuyConfigDataProvider } from '../../types/ProductLandingPageBuyConfig';
import { NavigationHelper } from '@nirvana/shared/navigation';

export class InProductTrialsAppView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $gzInProductTrialsHeader: GzInProductTrialsHeaderWebComponent;

    gzTrialError: {
      $image: GzImageRepoWebComponent;
      $title: GzTitleWebComponent;
      $text: GzTitleWebComponent;
      $link: GzLinkWebComponent;
    };

    $gzTrialHomePage: GzTrialHomePageWebComponent;
    $gzLandingPage: B2bLandingPageWebComponent;
  };

  private availableInProductTrialsDetails!: AvailableInProductTrialsDetails;
  private buyProductCardDataProvider: ProductLandingPageBuyConfigDataProvider;
  private gzTrialHomeTitle: string;

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $gzInProductTrialsHeader: this.shadow.querySelector('#gz-in-product-trials-header'),

      gzTrialError: {
        $image: this.shadow.querySelector('#product-trials-app-error-image'),
        $title: this.shadow.querySelector('#product-trials-app-error-title'),
        $text: this.shadow.querySelector('#product-trials-app-error-text'),
        $link: this.shadow.querySelector('#product-trials-app-error-link'),
      },

      $gzTrialHomePage: this.shadow.querySelector('#product-trials-app-home-page'),
      $gzLandingPage: this.shadow.querySelector('#product-trials-app-landing-page'),
    };

    this.setTranslatedStrings();
    this.setLoadingState();
  }

  setTranslatedStrings() {
    // title
    this.gzTrialHomeTitle = this.dependencies.translateFn('Products Hub');

    // home page
    this.dom.$gzTrialHomePage.texts.cardItemsTitle = this.dependencies.translateFn('Security Products and Add-ons');
    this.dom.$gzTrialHomePage.texts.titleEmptyStateText = this.dependencies.translateFn('More to come');
    this.dom.$gzTrialHomePage.texts.descriptionEmptyStateText = this.dependencies.translateFn(
      'New updates and products will be made available so you can try out additional security features.'
    );

    // landing page
    this.dom.$gzLandingPage.texts.title = this.dependencies.translateFn('Capabilities & Benefits');
    this.dom.$gzLandingPage.texts.faqTitle = this.dependencies.translateFn('FAQ');
  }

  public setWrapperTemplateState(templateState: TemplateState): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, templateState);
  }

  private setErrorContainerView(
    errorMessage: string,
    imageState: ImageState,
    errorTitle?: string,
    errorLink?: {
      href: string;
      text: string;
    }
  ): void {
    this.resetScrollBarCoordinates();
    this.setGzHomePageTitle();

    // set title if exists
    if (!errorTitle) {
      this.setElementClass(this.dom.gzTrialError.$title, 'hidden');
    } else {
      this.removeElementClass(this.dom.gzTrialError.$title, 'hidden');
      this.dom.gzTrialError.$title.innerText = errorTitle;
    }

    // set error link
    if (!errorLink) {
      this.setElementClass(this.dom.gzTrialError.$link, 'hidden');
    } else {
      this.removeElementClass(this.dom.gzTrialError.$link, 'hidden');
      this.dom.gzTrialError.$link.href = errorLink.href;
      this.dom.gzTrialError.$link.innerHTML = `<span>${htmlEncode(errorLink.text)}</span>`;
    }

    // set message and image state
    this.dom.gzTrialError.$text.innerText = errorMessage;
    this.dom.gzTrialError.$image.state = imageState;
  }

  buildSuccessView(viewData: InProductTrialsAppViewData) {
    if (viewData.availableInProductTrialsDetails.availableTrials.length === 0) {
      // empty state
      this.setErrorContainerView(
        this.dependencies.translateFn('No compatible products are currently available.'),
        ImageState.EMPTY,
        this.dependencies.translateFn('No products available'),
        {
          href: 'https://www.bitdefender.com/business/support/en/77209-262787-product-trials-hub.html',
          text: this.dependencies.translateFn('Learn more'),
        }
      );
      this.setWrapperTemplateState(TemplateState.EMPTY);
      return;
    }

    this.resetTemplateState(this.dom.$wrapper);

    this.availableInProductTrialsDetails = viewData.availableInProductTrialsDetails;
    this.buyProductCardDataProvider = viewData.buyProductCardDataProvider;

    this.showGzTrialHomePage();
  }

  buildErrorView() {
    // error state
    this.setErrorContainerView(
      this.dependencies.translateFn(
        'Data loading has failed. Please try to refresh the page and if the issue persists contact Bitdefender Enterprise Support for more information.'
      ),
      ImageState.ERROR
    );
    this.setWrapperTemplateState(TemplateState.ERROR);
  }

  public refreshLandingPageForTrialType(
    availableInProductTrialsDetails: AvailableInProductTrialsDetails,
    type: AvailableProductTrialType
  ) {
    this.availableInProductTrialsDetails = availableInProductTrialsDetails;
    this.showGzLandingPageForProductTrial(type);
  }

  public showGzLandingPageForProductTrial(type: AvailableProductTrialType) {
    this.resetScrollBarCoordinates();
    this.setGzLandingPageTitle(type);

    this.setElementClass(this.dom.$gzTrialHomePage, 'hidden');
    this.removeElementClass(this.dom.$gzLandingPage, 'hidden');

    const availableInProductTrial = AvailableInProductTrialsDetails.getAvailableInProductTrialForTrialTypeIfExists(
      type,
      this.availableInProductTrialsDetails
    );

    const gzLandingPageConfig: GzLandingPageConfig = getLandingPageConfigForTrialType(
      type,
      availableInProductTrial?.replacedKey
    );

    this.dom.$gzLandingPage.productItem = gzLandingPageConfig.productItem;
    this.dom.$gzLandingPage.cardItems = gzLandingPageConfig.cardItems;
    this.dom.$gzLandingPage.hasOutlineAroundAccordionItems = gzLandingPageConfig.hasOutlineAroundAccordionItems ?? true;
    this.dom.$gzLandingPage.accordionItems = gzLandingPageConfig.accordionItems;
    this.dom.$gzLandingPage.accordionItemsFaq = gzLandingPageConfig.accordionItemsFaq ?? [];

    const cannotBeStartedReason = availableInProductTrial?.cannotBeStartedReason;
    const cannotBeStartedUntil = availableInProductTrial?.cannotBeStartedUntil;

    this.dom.$gzLandingPage.setCannotBeStartedReason(cannotBeStartedReason);
    this.dom.$gzLandingPage.setAvailableOperation(availableInProductTrial?.availableOperation);
    this.dom.$gzLandingPage.setBuyConfig({
      enabled: availableInProductTrial?.canBuy ?? false,
      dataProvider: this.buyProductCardDataProvider,
    });

    if (cannotBeStartedReason && cannotBeStartedUntil) {
      const gzLandingPageCountdownTranslation: CountdownTranslations | undefined =
        getTranslationsForCountdownByReason(cannotBeStartedReason);

      this.dom.$gzLandingPage.dateToTimestamp = cannotBeStartedUntil;
      this.dom.$gzLandingPage.setCountdownTranslations(gzLandingPageCountdownTranslation);
    }
  }

  private showGzTrialHomePage() {
    this.resetScrollBarCoordinates();
    this.setGzHomePageTitle();

    this.setElementClass(this.dom.$gzLandingPage, 'hidden');
    this.removeElementClass(this.dom.$gzTrialHomePage, 'hidden');

    // get available main and secondary trials
    const availableTrials = AvailableInProductTrialsDetails.getMainAndSecondaryInProductTrials(
      this.availableInProductTrialsDetails
    );

    this.dom.$gzTrialHomePage.productItems = getHomePageMainProductItemsConfig(availableTrials.mainProducts);
    this.dom.$gzTrialHomePage.cardItems = getHomePageSecondaryCardItemsConfig(availableTrials.secondaryProducts);
  }

  private setGzHomePageTitle() {
    this.dom.$gzInProductTrialsHeader.headerItems = [{ text: this.gzTrialHomeTitle }];
  }

  private setGzLandingPageTitle(type: AvailableProductTrialType) {
    this.dom.$gzInProductTrialsHeader.headerItems = [
      {
        text: this.gzTrialHomeTitle,
        onClickFunction: () => {
          NavigationHelper.navigateToProductsHub();
        },
      },
      { text: getTranslatedInProductTrialType(type) },
    ];
  }

  private resetScrollBarCoordinates() {
    this.dom.$wrapper.scrollTo(0, 0);
  }
}
